<template>
    <CardOption
        class="card-icon"
        v-bind="{ card, questionnaireStore, isSelected }"
        @click.prevent.stop="$emit('toggle', !isSelected)"
    >
        <CmsImage
            :data="content.icon"
            class="the-icon"
            fit="contain"
            :sizes="['25vw']"
        />

        <h2 class="title">
            {{ content.title }}
        </h2>

        <div class="description">
            {{ content.description }}
        </div>
    </CardOption>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import type { QuestionnaireQuestionChoiceOptionContentIconCardFragment } from '@/graphql/api'

import type { QuestionnaireStore } from '@/components/cms/component/questionnaire/useQuestionnaireStore'
import { cmsColorToButtonColor } from '@/components/cms'
import { getColorForBackground } from '@/components/ui/buttons'
import { getOptionContent } from '../composables/evaluateConditions.ts'

import CardOption from './CardOption.vue'
import CmsImage from '@/components/cms/CmsImage.vue'

const props = defineProps<{
    card: QuestionnaireQuestionChoiceOptionContentIconCardFragment
    questionnaireStore: QuestionnaireStore
    isSelected: boolean | undefined
}>()

defineEmits<{
    'toggle': [select: boolean]
}>()

const backgroundColor = computed(() =>
    cmsColorToButtonColor(props.card.backgroundColor)
)
const color = computed(() => getColorForBackground(backgroundColor.value))

const route = useRoute()

const content = computed(() =>
    getOptionContent(
        props.card,
        props.questionnaireStore,
        route.params['id'] as string
    )
)
</script>

<style lang="scss" scoped>
.card-icon {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: v-bind(backgroundColor);
    color: v-bind(color);

    .the-icon {
        object-fit: contain;
        margin-top: 72px;

        @include min-width(640) {
            margin-top: 154px;
        }
    }

    .title {
        margin-top: 56px;
        text-align: center;
        font-family: $parcel-font-stack;
        font-style: normal;
        font-size: 1.25rem;
        font-weight: 300;
        text-transform: uppercase;

        @include min-width(640) {
            margin: 120px 0 20px;
            font-size: 1.5rem;
        }
    }

    .description {
        text-align: center;
        font-family: $sectra-font-stack;
        font-size: 0.75rem;
        font-weight: 350;

        @include min-width(640) {
            font-size: 1rem;
            line-height: 145%;
        }
    }
}
</style>
